import { getTimestampGranularity } from '../features/metricValue';
import { isDefaultLineStaticRefLine } from '../features/threshold';
import {
  definitionWithStatus,
  getAuthoredStatus,
  NullableStatus,
  NullStatus,
  SimpleStatus,
  ThresholdStatus,
} from '../features/status';
import {
  DateWindow,
  defaultDateWindow,
  definitionWithWindow,
  displayStringFromWindow,
  getFirstWindow,
  getFirstWindowOrDefault,
  isCustomWindow,
  isDefinedStartWindow,
} from '../features/window';
import { getAuthoredComparisonText } from '../formatting/comparisonText';
import { dateFormatterFactory } from '../formatting/dateFormatter';
import { ComparisonSpecType, dateTruncFromGranularity, EffectiveDefinition, shouldShowComparisonLine } from '../models';
import { definitionWithComparison, getComparisonFromDefinitionAndFlags } from '../features/comparison';

export interface ConfigurationText {
  readonly comparisonText?: string;
  readonly windowText?: string;
}

export interface MetricConfig {
  readonly comparison: ComparisonSpecType | null;
  readonly window: DateWindow | null;
  readonly status: NullableStatus;
}

export const buildConfigurationText = (definition: EffectiveDefinition, isSingleNumber: boolean): ConfigurationText => {
  const isConstantComparison = isDefaultLineStaticRefLine(definition);

  const showWindowText = !isSingleNumber;
  const showComparisonText = (isSingleNumber && isConstantComparison) || !isSingleNumber;

  const comparisonText = getAuthoredComparisonText(definition);
  const serverWindow = getFirstWindow(definition);
  let windowText = serverWindow && displayStringFromWindow(serverWindow);

  if (serverWindow && isCustomWindow(serverWindow)) {
    const startDate = serverWindow.range.first;
    const dateTrunc = dateTruncFromGranularity(getTimestampGranularity(definition));
    windowText = dateFormatterFactory(dateTrunc).configurationInfoStartDate(startDate);
  } else if (serverWindow && isDefinedStartWindow(serverWindow)) {
    const dateTrunc = dateTruncFromGranularity(getTimestampGranularity(definition));
    windowText = dateFormatterFactory(dateTrunc).configurationInfoStartDate(serverWindow.startDate);
  }

  return {
    comparisonText: showComparisonText ? comparisonText : undefined,
    windowText: showWindowText ? windowText : undefined,
  };
};

export const getConfigurationFromDefinition = (
  flags: {
    areDateWindowsEnabled?: boolean;
    isColorStatusIndicatorsEnabled?: boolean;
    isHistoricalComparisonsEnabled?: boolean;
    isReferenceLineEnabled?: boolean;
  },
  definition: EffectiveDefinition
): MetricConfig => {
  const comparison = getComparisonFromDefinitionAndFlags(flags, definition) ?? null;

  const dateTrunc = dateTruncFromGranularity(getTimestampGranularity(definition));
  const window = flags.areDateWindowsEnabled ? getFirstWindowOrDefault(definition) : defaultDateWindow(dateTrunc);

  const status = flags.isColorStatusIndicatorsEnabled ? getAuthoredStatus(definition) : NullStatus;

  return { comparison, window, status };
};

export function definitionFromConfig(config: MetricConfig, defToStartWith: EffectiveDefinition): EffectiveDefinition {
  let definition = defToStartWith;
  if (config.window) {
    definition = definitionWithWindow(definition, config.window);
  }
  if (config.comparison) {
    definition = definitionWithComparison(definition, config.comparison, !shouldShowComparisonLine(config.comparison));
    if (config.comparison.type === 'STATIC_COMPARISON') {
      definition = definitionWithStatus(definition, nonNullStatus(config.status)).unwrap();
    }
  }
  return definition;
}

const nonNullStatus = (status: NullableStatus): SimpleStatus => {
  return {
    above: status.above ?? ThresholdStatus.UNKNOWN,
    equal: status.equal ?? ThresholdStatus.UNKNOWN,
    below: status.below ?? ThresholdStatus.UNKNOWN,
  };
};
